import { memo, useCallback, useState } from 'react'
import { FileText } from 'lucide-react'
import type { TranscriptEntry } from '@highlight/common'
import { useCopyTranscript } from '@renderer/hooks/use-audio-note-copy'
import { useAudioTranscript } from '@renderer/hooks/audio/use-audio-transcript'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { SavedMeetingNoteTranscript } from '../../../components/meeting/meeting-note-content'
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
} from '../../../components/meeting/empty-state'
import type { TranscriptJump } from '../../../components/meeting/transcript'
import { MeetingCapturedActions } from './captured-actions'
import { useMeetingSessionId } from '../action-items/use-meeting-session-id'
import { LiveTranscript } from './live-transcript'
import type { MeetingViewContext, MeetingViewModel } from '../use-meeting-view-model'
import { useViewContext } from '../../../view-context'
import { useClaimFloatingBarCopy } from '../../../atoms'

interface MeetingViewTranscriptPanelProps {
  model: MeetingViewModel
  /** The one-column layout leads the transcript with the action items it captured. */
  showsCapturedActions: boolean
  /** The open tab, so only the panel on screen offers the bar its copy. */
  isActiveTab: boolean
  onContentChange: () => void
  pinToBottom: () => boolean
  stopFollowing: () => void
  scrollElement: HTMLDivElement | null
}

/**
 * The Transcript tab: speaker/timestamp turns, streaming while the meeting is
 * live. No section header, the tab already names it, so the copy control
 * keeps a slim row of its own at the top.
 */
export const MeetingViewTranscriptPanel = memo(function MeetingViewTranscriptPanel({
  model,
  showsCapturedActions,
  isActiveTab,
  onContentChange,
  pinToBottom,
  stopFollowing,
  scrollElement,
}: MeetingViewTranscriptPanelProps) {
  const isLive = model.variant === 'live'
  // The view's own context carries the ids a live entry latches, so the session survives main clearing it at save
  const { context } = useViewContext<MeetingViewContext>()
  const sessionId = useMeetingSessionId({
    ...context,
    mode: context?.mode ?? (isLive ? 'live' : 'note'),
    audioNoteId: context?.audioNoteId ?? model.audioNoteId ?? undefined,
  })
  const [jumpTo, setJumpTo] = useState<TranscriptJump | null>(null)
  const jump = useCallback(
    (target: TranscriptJump) => {
      stopFollowing()
      setJumpTo(target)
    },
    [stopFollowing],
  )

  return (
    <section aria-label="Transcript" className="flex flex-col px-5 pb-5 pt-1">
      {showsCapturedActions && (
        <MeetingCapturedActions
          audioSessionId={sessionId}
          audioNoteId={isLive ? undefined : (model.audioNoteId ?? undefined)}
          onJump={isLive ? jump : undefined}
        />
      )}
      {/* No copy control of its own: the floating bar carries one for whichever
          tab is open, and this publishes what it should copy. */}
      {isLive ? (
        <LiveTranscriptCopyClaim active={isActiveTab} />
      ) : (
        <TranscriptCopyClaim active={isActiveTab} entries={model.audioNote?.transcriptEntries} />
      )}
      {/* The air the removed copy row used to give the first speaker — 16px
          with the section's own 4px, where the row itself came to 28px and
          read as too much once it was empty space rather than a control. On
          the content rather than the section, so the captured actions above
          still sit tight to the top, and so it applies whether or not they
          are there. */}
      <div className="flex flex-col pt-3">
        {isLive ? (
          <LiveTranscript
            onContentChange={onContentChange}
            pinToBottom={pinToBottom}
            scrollElement={scrollElement}
            jumpTo={jumpTo}
          />
        ) : model.audioNote ? (
          <SavedMeetingNoteTranscript audioNote={model.audioNote} virtualize scrollElement={scrollElement} />
        ) : (
          <EmptyState className="px-5 py-10">
            <EmptyStateIcon>
              <FileText />
            </EmptyStateIcon>
            <EmptyStateTitle>No transcript yet</EmptyStateTitle>
            <EmptyStateDescription>This meeting hasn’t been recorded.</EmptyStateDescription>
          </EmptyState>
        )}
      </div>
    </section>
  )
})

/** Live variant: publishes whatever the streaming transcript holds right now. */
function LiveTranscriptCopyClaim({ active }: { active: boolean }) {
  const { entries } = useAudioTranscript()
  return <TranscriptCopyClaim active={active} entries={entries} />
}

function TranscriptCopyClaim({ active, entries }: { active: boolean; entries: Array<TranscriptEntry> | undefined }) {
  const { handleCopy, hasTranscript } = useCopyTranscript(entries)
  useClaimFloatingBarCopy(
    active &&
      hasTranscript && {
        label: 'Copy transcript',
        copy: () => {
          void handleCopy()
          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'copy transcript',
            trigger: 'sidepanel meeting view',
          })
        },
      },
  )
  return null
}
